import { motion } from "framer-motion";
import { Target, ShieldCheck, HandCoins, Layers, Handshake, Truck, Cpu, Ship } from "lucide-react";
import { IMG, BUSINESS_TYPES } from "@/config";
import { PageHero, SectionHeading } from "@/components/Section";
import { Reveal, stagger, fadeUp } from "@/components/Reveal";
import EditorialMarquee from "@/components/EditorialMarquee";
import { PortalButton } from "@/components/PortalButton";

const VALUES = [
  { icon: ShieldCheck, t: "Reliability", d: "Dependable stock and deliveries that arrive when expected." },
  { icon: Layers, t: "Transparency", d: "Clear pricing, clear invoices and clear communication." },
  { icon: HandCoins, t: "Competitive Pricing", d: "Direct sourcing that keeps your food costs in check." },
  { icon: Truck, t: "Flexibility", d: "Delivery schedules built around your operation." },
  { icon: Target, t: "Accuracy", d: "Careful order preparation to reduce costly errors." },
  { icon: Handshake, t: "Long-Term Partnerships", d: "We grow with the businesses we supply." },
];

const PILLARS = [
  { icon: Layers, t: "Our Warehouse", d: "A well-managed facility with organized racking, category storage and a trained team focused on accurate order picking." },
  { icon: Ship, t: "Our Import Network", d: "Direct sourcing from trusted manufacturers in India and China for better pricing and dependable availability." },
  { icon: HandCoins, t: "Our Commitment to Pricing", d: "Competitive wholesale pricing plus a price match and price beat program on eligible items." },
  { icon: Cpu, t: "Our Technology", d: "Modern online ordering, digital invoices and purchasing reports that save your team hours every week." },
];

export default function About() {
  return (
    <>
      <PageHero
        chapter="About Us"
        title="Built to make restaurant purchasing easier"
        lead="Brampton Cash & Carry was created to give restaurants and food businesses a more convenient way to purchase ingredients, packaging and essential supplies."
        image={IMG.workers}
      >
        <PortalButton variant="butter" testId="about-open-account-btn">Open a Wholesale Account</PortalButton>
      </PageHero>

      {/* Mission */}
      <section className="py-24 md:py-32 bg-cream">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-14 items-center">
          <SectionHeading
            chapter="Our Mission"
            overline="Why We Exist"
            title={<>Dependable stock, fair pricing, accurate orders</>}
            lead="We understand that restaurant owners need dependable stock, fair pricing, accurate orders and deliveries that arrive when expected. Our goal is to build long-term relationships with food businesses by combining a well-managed wholesale warehouse, direct sourcing, flexible delivery and modern ordering technology."
          />
          <Reveal delay={0.1}>
            <div className="relative">
              <div className="absolute -inset-3 rounded-[2rem] bg-butter rotate-2" />
              <img src={IMG.warehouseShelves} alt="Organized warehouse aisles" className="relative rounded-[1.75rem] object-cover w-full h-[440px] border border-forest/15" />
            </div>
          </Reveal>
        </div>
      </section>

      <EditorialMarquee tone="butter" />

      {/* Values */}
      <section className="py-24 md:py-32 bg-white border-y border-forest/10">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10">
          <SectionHeading chapter="Our Values" overline="What We Stand For" title="The principles behind every order" className="mb-14" />
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {VALUES.map(({ icon: Icon, t, d }) => (
              <motion.div key={t} variants={fadeUp} className="rounded-3xl border border-forest/15 bg-cream p-7 hover:shadow-hard transition-shadow duration-300">
                <span className="grid place-items-center h-12 w-12 rounded-2xl bg-sage text-forest mb-4"><Icon className="h-5 w-5" /></span>
                <h3 className="font-display text-xl text-forest">{t}</h3>
                <p className="text-sm text-forest/70 mt-2 leading-relaxed">{d}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Who we serve */}
      <section className="py-24 md:py-32 bg-sage/50">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10">
          <SectionHeading chapter="Who We Serve" overline="Our Customers" title="Food businesses of every kind" className="mb-10" />
          <div className="flex flex-wrap gap-2.5">
            {BUSINESS_TYPES.map((b) => (
              <span key={b} className="rounded-full bg-white border border-forest/20 px-4 py-2 text-sm font-medium text-forest">{b}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-24 md:py-32 bg-cream">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10">
          <SectionHeading chapter="How We Operate" overline="Our Foundations" title="Warehouse, imports, pricing & technology" className="mb-14" />
          <div className="grid md:grid-cols-2 gap-6">
            {PILLARS.map(({ icon: Icon, t, d }) => (
              <Reveal key={t}>
                <div className="flex gap-5 rounded-3xl border border-forest/15 bg-white p-7 h-full">
                  <span className="grid place-items-center h-12 w-12 rounded-2xl bg-forest text-butter shrink-0"><Icon className="h-5 w-5" /></span>
                  <div>
                    <h3 className="font-display text-xl text-forest">{t}</h3>
                    <p className="text-sm text-forest/70 mt-2 leading-relaxed">{d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
